import React, { useEffect, useState } from "react";
import { Row, Col, Statistic, Typography, List, Flex, Empty, Spin, Skeleton, Tooltip, Progress } from "antd";
import {
    TeamOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";
import CollapsibleInsightCard from "../CollapsibleInsightCard";
import { JobDescriptionEntity } from "../../../../../../../Entities/JobDescription";
import { CHART_COLORS } from "../../../../StatsAndAnalytics/utils/chartColors";
import jobApplicationService from "../../../../../../../Service/job.application.service";
import "./index.scss";

const { Text } = Typography;

// Interviewer stat interface (matches backend response)
interface InterviewerStat {
    id: string;
    name: string;
    email: string;
    stats: {
        upcoming: number;
        completed: number;
        total: number;
    };
}

interface InterviewerWorkloadData {
    summary: {
        totalInterviewers: number;
        totalUpcoming: number;
        totalCompleted: number;
        totalPendingSlot: number;
    };
    interviewers: InterviewerStat[];
}

interface InterviewersInsightCardProps {
    jobPost: JobDescriptionEntity;
    themeColor?: string;
    /** Controlled mode: external expanded state */
    expanded?: boolean;
    /** Controlled mode: callback when expand/collapse is toggled */
    onExpandChange?: (expanded: boolean) => void;
    /** Called when a stat card is clicked */
    onStatClick?: (status: "upcoming" | "completed" | "pending") => void;
}

// Helper function to format time ago
const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);

    if (diffSecs < 60) {
        return "just now";
    } else if (diffMins < 60) {
        return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
    } else if (diffHours < 24) {
        const mins = diffMins % 60;
        return mins > 0 
            ? `${diffHours}h ${mins}m ago`
            : `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    }
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Skeleton loader for stat cards
const StatCardSkeleton: React.FC = () => (
    <div className="stat-box">
        <Skeleton.Input active size="small" style={{ width: 60, marginBottom: 8 }} />
        <Skeleton.Input active size="large" style={{ width: 40 }} />
    </div>
);

// Skeleton loader for interviewer item
const InterviewerItemSkeleton: React.FC = () => (
    <List.Item className="interviewer-item">
        <Flex vertical style={{ width: "100%" }} gap={8}>
            <Skeleton.Input active size="small" style={{ width: 150 }} />
            <Skeleton.Input active size="small" style={{ width: 200 }} />
            <Skeleton active paragraph={{ rows: 1, width: ['100%'] }} title={false} />
        </Flex>
    </List.Item>
);

const InterviewersInsightCard: React.FC<InterviewersInsightCardProps> = ({
    jobPost,
    themeColor,
    expanded,
    onExpandChange,
    onStatClick,
}) => {
    const [data, setData] = useState<InterviewerWorkloadData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastSynced, setLastSynced] = useState<Date | null>(null);
    const [, setForceUpdate] = useState(0); // For re-rendering time ago

    // Fetch interviewer workload data
    const fetchData = async (isRefresh = false) => {
        if (!jobPost?._id) return;

        setLoading(true);
        setError(null);
        try {
            const response = await jobApplicationService.getInterviewerWorkload(jobPost._id);
            setData(response as InterviewerWorkloadData);
            setLastSynced(new Date());
        } catch (err) {
            console.error("Failed to fetch interviewer workload:", err);
            setError("Failed to load insights");
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchData();
    }, [jobPost?._id]);

    // Update "time ago" display every minute
    useEffect(() => {
        const interval = setInterval(() => {
            if (lastSynced) {
                setForceUpdate((prev) => prev + 1);
            }
        }, 60000); // Update every minute

        return () => clearInterval(interval);
    }, [lastSynced]);

    // Handle refresh
    const handleRefresh = () => {
        fetchData(true);
    };

    const hasInterviewers = jobPost?.interviewers && jobPost.interviewers.length > 0;

    // Generate subtitle with last synced time
    const subtitle = lastSynced ? `Last synced: ${getTimeAgo(lastSynced)}` : undefined;

    if (!hasInterviewers) {
        return (
            <CollapsibleInsightCard
                title="Interviewers"
                subtitle={subtitle}
                icon={<TeamOutlined />}
                onRefresh={handleRefresh}
                isRefreshing={loading}
                themeColor={themeColor}
                expanded={expanded}
                onExpandChange={onExpandChange}
            >
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No interviewers assigned"
                />
            </CollapsibleInsightCard>
        );
    }

    // Determine if we're in initial load or refresh state
    const isInitialLoad = loading && !data;
    const isRefreshing = loading && !!data;

    // Check if we have workload data
    const hasWorkloadData = data && (data.summary.totalUpcoming > 0 || data.summary.totalCompleted > 0 || data.summary.totalPendingSlot > 0);
    const maxWorkload = data?.interviewers.reduce((max, i) => Math.max(max, i.stats.total), 0) || 1;

    // Summary content (collapsed view)
    const summaryContent = (
        <div className="interviewers-summary">
            {isInitialLoad ? (
                <Flex justify="center" align="center" style={{ minHeight: 100 }}>
                    <Spin size="small" />
                </Flex>
            ) : error ? (
                <Text type="danger">{error}</Text>
            ) : (
                <>
                    {/* Stats Row - Interview Metrics */}
                    <Row gutter={[12, 12]} className="stats-row">
                        <Col xs={8}>
                            {isRefreshing ? (
                                <StatCardSkeleton />
                            ) : (
                                <Tooltip title="Interviews scheduled for the future">
                                    <div
                                        className="stat-box clickable"
                                        onClick={() => onStatClick?.("upcoming")}
                                    >
                                        <Statistic
                                            title="Upcoming"
                                            value={data?.summary.totalUpcoming || 0}
                                            valueStyle={{ fontSize: 20, fontWeight: 600, color: CHART_COLORS.primary }}
                                        />
                                    </div>
                                </Tooltip>
                            )}
                        </Col>
                        <Col xs={8}>
                            {isRefreshing ? (
                                <StatCardSkeleton />
                            ) : (
                                <Tooltip title="Candidates waiting to select interview slot">
                                    <div
                                        className="stat-box clickable"
                                        onClick={() => onStatClick?.("pending")}
                                    >
                                        <Statistic
                                            title="Pending Slot"
                                            value={data?.summary.totalPendingSlot || 0}
                                            valueStyle={{ fontSize: 20, fontWeight: 600, color: CHART_COLORS.warning }}
                                        />
                                    </div>
                                </Tooltip>
                            )}
                        </Col>
                        <Col xs={8}>
                            {isRefreshing ? (
                                <StatCardSkeleton />
                            ) : (
                                <Tooltip title="Interviews that have already taken place">
                                    <div
                                        className="stat-box clickable"
                                        onClick={() => onStatClick?.("completed")}
                                    >
                                        <Statistic
                                            title="Completed"
                                            value={data?.summary.totalCompleted || 0}
                                            valueStyle={{ fontSize: 20, fontWeight: 600, color: CHART_COLORS.success }}
                                        />
                                    </div>
                                </Tooltip>
                            )}
                        </Col>
                    </Row>

                    {/* No data message */}
                    {!hasWorkloadData && (
                        <Empty 
                            description="No interview data yet" 
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{ marginTop: 16 }}
                        />
                    )}
                </>
            )}
        </div>
    );

    // Expanded content (detailed view) - Per interviewer workload
    const expandedContent = isInitialLoad ? null : (
        <div className="interviewers-details">
            {isRefreshing ? (
                <>
                    <InterviewerItemSkeleton />
                    <InterviewerItemSkeleton />
                </>
            ) : (
                <List
                    size="small"
                    dataSource={data?.interviewers || []}
                    locale={{ emptyText: <Empty description="No interviewer data" /> }}
                    renderItem={(item) => (
                        <List.Item className="interviewer-item">
                            <Flex vertical style={{ width: "100%" }} gap={8}>
                                {/* Interviewer Info */}
                                <Flex justify="space-between" align="center">
                                    <Flex vertical gap={2}>
                                        <Text strong style={{ fontWeight: 500 }}>{item.name}</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{item.email}</Text>
                                    </Flex>
                                    <Text strong style={{ fontSize: 14 }}>
                                        {item.stats.total} interview{item.stats.total !== 1 ? 's' : ''}
                                    </Text>
                                </Flex>

                                {/* Workload Bar */}
                                <Tooltip title={`${item.stats.upcoming} upcoming, ${item.stats.completed} completed`}>
                                    <Progress
                                        percent={maxWorkload > 0 ? Math.round((item.stats.total / maxWorkload) * 100) : 0}
                                        size="small"
                                        strokeColor={themeColor || CHART_COLORS.primary}
                                        showInfo={false}
                                    />
                                </Tooltip>

                                {/* Stats breakdown */}
                                <Flex gap={16} className="stats-breakdown">
                                    <Text style={{ fontSize: 12 }}>
                                        <CalendarOutlined style={{ color: CHART_COLORS.primary, marginRight: 4 }} />
                                        {item.stats.upcoming} upcoming
                                    </Text>
                                    <Text style={{ fontSize: 12 }}>
                                        <CheckCircleOutlined style={{ color: CHART_COLORS.success, marginRight: 4 }} />
                                        {item.stats.completed} completed
                                    </Text>
                                </Flex>
                            </Flex>
                        </List.Item>
                    )}
                />
            )}
        </div>
    );

    return (
        <CollapsibleInsightCard
            title="Interviewers"
            subtitle={subtitle}
            icon={<TeamOutlined />}
            onRefresh={handleRefresh}
            isRefreshing={loading}
            themeColor={themeColor}
            expandedContent={expandedContent}
            expanded={expanded}
            onExpandChange={onExpandChange}
        >
            {summaryContent}
        </CollapsibleInsightCard>
    );
};

export default InterviewersInsightCard;







